import { PROGRAM_NAMES, defaultLinuxGroupsForProgram } from '../pma/pmaIdentityPolicy';
import { defaultDurationToken } from '../pma/pmaLifecyclePolicy';

export const DEFAULT_RESERVED_UNIX_FORM = Object.freeze({
  org: '',
  slug: '',
  prefix: '',
  count: 30,
  start: 1,
  emailDomain: '',
  programType: 'WS',
  userCategory: 'bulk',
  mode: 'Online',
  center: '',
  duration: '15d',
  linuxGroup: 'ws',
});

export const EMPTY_RESERVED_UNIX_TOUCHED = Object.freeze({
  slug: false,
  prefix: false,
  emailDomain: false,
  linuxGroup: false,
});

export function slugifyInstitution(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
    .slice(0, 16);
}

export function defaultUnixPrefix(slug) {
  const stem = slugifyInstitution(slug);
  return stem ? `ws_${stem}user` : '';
}

export function defaultEmailDomain(slug) {
  const stem = slugifyInstitution(slug);
  return stem ? `${stem}.example.com` : '';
}

function withDerived(form, touched = EMPTY_RESERVED_UNIX_TOUCHED) {
  const slug = touched.slug ? slugifyInstitution(form.slug) : slugifyInstitution(form.org);
  const prefix = touched.prefix ? String(form.prefix || '').trim().toLowerCase() : defaultUnixPrefix(slug);
  const emailDomain = touched.emailDomain
    ? String(form.emailDomain || '').trim().toLowerCase()
    : defaultEmailDomain(slug);
  return { ...form, slug, prefix, emailDomain };
}

export function formFromPreset(preset) {
  if (!preset || typeof preset !== 'object') return { ...DEFAULT_RESERVED_UNIX_FORM };
  return {
    ...DEFAULT_RESERVED_UNIX_FORM,
    org: preset.org || '',
    slug: preset.slug || slugifyInstitution(preset.org),
    prefix: preset.prefix || defaultUnixPrefix(preset.slug || preset.org),
    count: Number(preset.count) || DEFAULT_RESERVED_UNIX_FORM.count,
    start: Number(preset.start) || DEFAULT_RESERVED_UNIX_FORM.start,
    emailDomain: preset.emailDomain || defaultEmailDomain(preset.slug || preset.org),
    programType: preset.programType || DEFAULT_RESERVED_UNIX_FORM.programType,
    userCategory: preset.userCategory || DEFAULT_RESERVED_UNIX_FORM.userCategory,
    mode: preset.mode || DEFAULT_RESERVED_UNIX_FORM.mode,
    center: preset.center || '',
    duration: preset.duration || DEFAULT_RESERVED_UNIX_FORM.duration,
    linuxGroup: preset.linuxGroup || DEFAULT_RESERVED_UNIX_FORM.linuxGroup,
  };
}

export function applyReservedUnixFormChange(form, field, value, touched = EMPTY_RESERVED_UNIX_TOUCHED) {
  const nextTouched = { ...touched };
  if (field === 'slug' || field === 'prefix' || field === 'emailDomain' || field === 'linuxGroup') {
    nextTouched[field] = true;
  }
  if (field === 'org') {
    nextTouched.slug = false;
    if (!touched.prefix) nextTouched.prefix = false;
    if (!touched.emailDomain) nextTouched.emailDomain = false;
  }

  let next = { ...form, [field]: value };
  if (field === 'programType') {
    next.duration = defaultDurationToken({
      programType: value,
      userCategory: next.userCategory,
    });
    if (!touched.linuxGroup) {
      next.linuxGroup = defaultLinuxGroupsForProgram(value, next.linuxGroup ? [next.linuxGroup] : [])[0] || '';
      if (value === 'WS' && (next.linuxGroup === 'po' || !next.linuxGroup)) next.linuxGroup = 'ws';
      if (value === 'CG' && (next.linuxGroup === 'po' || !next.linuxGroup)) next.linuxGroup = 'cg';
    }
  }
  next = withDerived(next, nextTouched);
  return { form: next, touched: nextTouched };
}

export function reservedUnixPlanSummary(form, touched = EMPTY_RESERVED_UNIX_TOUCHED) {
  const resolved = withDerived(form, touched);
  const count = Number(resolved.count);
  const start = Number(resolved.start);
  const safeCount = Number.isInteger(count) && count >= 1 ? count : 0;
  const safeStart = Number.isInteger(start) && start >= 1 ? start : 1;
  const last = safeStart + Math.max(safeCount, 1) - 1;
  const firstUnix = resolved.prefix ? `${resolved.prefix}${safeStart}` : '';
  const lastUnix = resolved.prefix ? `${resolved.prefix}${last}` : '';
  const firstEmail = resolved.prefix && resolved.emailDomain
    ? `${resolved.prefix}${safeStart}@${resolved.emailDomain}`
    : '';
  const lastEmail = resolved.prefix && resolved.emailDomain
    ? `${resolved.prefix}${last}@${resolved.emailDomain}`
    : '';
  return {
    org: resolved.org,
    slug: resolved.slug,
    prefix: resolved.prefix,
    programName: PROGRAM_NAMES[resolved.programType] || resolved.programType,
    firstUnix,
    lastUnix,
    firstEmail,
    lastEmail,
    count: safeCount,
    start: safeStart,
  };
}

export function reservedUnixPlanPayload(form, touched = EMPTY_RESERVED_UNIX_TOUCHED) {
  const resolved = withDerived(form, touched);
  return {
    org: resolved.org,
    slug: resolved.slug,
    prefix: resolved.prefix,
    count: Number(resolved.count),
    start: Number(resolved.start),
    emailDomain: resolved.emailDomain,
    programType: resolved.programType,
    userCategory: resolved.userCategory,
    mode: resolved.mode,
    center: resolved.mode === 'Online' ? '' : resolved.center,
    duration: resolved.duration,
    linuxGroup: resolved.linuxGroup,
  };
}
